import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { JOINT_NAMES, type CharacterRig, type JointName } from './build';
import type { CharacterSpec } from './spec';

/**
 * Laadt een geskind personage uit een GLB en levert het als `CharacterRig` op.
 *
 * Dit is de tweede manier om aan een rig te komen, naast `buildCharacter()`. De
 * procedurele builder blijft de norm voor NPC's en achtergrondvolk; hero-personages
 * komen uit Blender omdat hun kleding en gezicht niet uit primitieven te halen zijn.
 * Zie `docs/CHARACTERS.md`.
 *
 * De GLB is zo geëxporteerd dat hij hier niets bijzonders nodig heeft:
 *
 * - de botnamen zijn exact `JOINT_NAMES`, dus `CharacterAnimator` stuurt hem ongewijzigd
 *   aan;
 * - elke bot heeft een **identity rest-rotatie**, want de animator zet rotaties absoluut
 *   (`rotation.x = hoek`). Een bot met een rest-rotatie zou bij het eerste frame
 *   wegklappen;
 * - `pelvis` staat op y = `hipHeight`, wat de animator per frame overschrijft voor de
 *   loop-bob;
 * - het model staat met de voeten op y = 0 en kijkt langs +Z, gelijk aan de conventie in
 *   `docs/ARCHITECTURE.md`.
 *
 * Het exportscript staat in de asset-repo: `scripts/export_luca_rig_glb.py`.
 */

/** Hoe ver een botrotatie van identity mag afwijken voor we hem afkeuren. */
const REST_ROTATION_TOLERANCE = 1e-3;

export interface LoadedRig extends CharacterRig {
  /** Voor het opruimen van geometrie en textures bij het verwijderen van het personage. */
  dispose(): void;
}

export async function loadCharacterRig(url: string, spec: CharacterSpec): Promise<LoadedRig> {
  const gltf = await new GLTFLoader().loadAsync(url);

  const root = new THREE.Group();
  root.name = `NA_Char_${spec.name}`;
  root.add(gltf.scene);

  const joints = {} as Record<JointName, THREE.Object3D>;
  for (const name of JOINT_NAMES) {
    const joint = gltf.scene.getObjectByName(name);
    if (!joint) {
      throw new Error(
        `Rig ${url} mist het gewricht "${name}". De GLB moet botten hebben met exact de ` +
          `namen uit JOINT_NAMES; her-exporteer met scripts/export_luca_rig_glb.py.`,
      );
    }
    assertRestRotationIsIdentity(joint, url);
    joints[name] = joint;
  }

  const meshes: THREE.Mesh[] = [];
  gltf.scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Een SkinnedMesh houdt zijn bounding volume uit de rustpose aan, dus bij een
    // uitgestrekte arm knippert hij weg aan de rand van het beeld. Eén hero-personage
    // kost het niet om de culling over te slaan.
    mesh.frustumCulled = false;
    meshes.push(mesh);
  });

  return {
    root,
    joints,
    spec,
    deformation: 'skinned',
    dispose() {
      for (const mesh of meshes) {
        mesh.geometry.dispose();
        for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
          disposeMaterial(material);
        }
      }
    },
  };
}

/**
 * De animator zet `joint.rotation.x` absoluut in plaats van te vermenigvuldigen met de
 * rustpose. Dat mag alleen als de bot in rust op identity staat — anders is de eerste
 * frame-update meteen een verkeerde pose. Liever hier hard falen dan een personage dat
 * ondersteboven door de haven loopt.
 */
function assertRestRotationIsIdentity(joint: THREE.Object3D, url: string): void {
  const { x, y, z, w } = joint.quaternion;
  const identity =
    Math.abs(x) < REST_ROTATION_TOLERANCE &&
    Math.abs(y) < REST_ROTATION_TOLERANCE &&
    Math.abs(z) < REST_ROTATION_TOLERANCE &&
    Math.abs(Math.abs(w) - 1) < REST_ROTATION_TOLERANCE;

  if (!identity) {
    throw new Error(
      `Rig ${url}: gewricht "${joint.name}" heeft een rest-rotatie ` +
        `(${x.toFixed(4)}, ${y.toFixed(4)}, ${z.toFixed(4)}, ${w.toFixed(4)}) in plaats van ` +
        `identity. Exporteer de botten parallel (kop naar +Z, roll 0) zodat de animator ` +
        `rotaties absoluut mag zetten.`,
    );
  }
}

function disposeMaterial(material: THREE.Material): void {
  for (const value of Object.values(material)) {
    if (value && typeof value === 'object' && 'isTexture' in value) {
      (value as THREE.Texture).dispose();
    }
  }
  material.dispose();
}
